import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { HiOutlineChartBar, HiOutlineBan } from 'react-icons/hi';
import { TbWallet } from 'react-icons/tb';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { shortAddress } from '../services/wallet';
import { IncomeSkeleton } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';

function sourceLabel(type) {
  if (type === 'self_daily') return 'Self daily';
  if (type === 'team_purchase') return 'Team purchase';
  if (type === 'rank_bonus') return 'Rank bonus';
  if (type === 'roi_upline') return 'ROI upline';
  if (type === 'referral_pool') return 'Referral pool';
  if (type === 'claim_charge') return 'Claim charge';
  return type || '—';
}

export default function IncomePage() {
  const { user, openConnect, connectOpening, loading } = useWallet();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [events, setEvents] = useState([]);
  const [cap, setCap] = useState(null);
  const [filter, setFilter] = useState('all');
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setPageLoading(false);
      return;
    }
    let cancelled = false;
    setPageLoading(true);
    api
      .myIncome()
      .then((d) => {
        if (cancelled) return;
        setEvents(d.events || []);
        setCap(d.cap || null);
      })
      .catch((e) => {
        if (!cancelled) toastRef.current.error(e.message || 'Failed to load income report');
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.wallet_address, loading]);

  if (loading || (user && pageLoading)) {
    return <IncomeSkeleton />;
  }

  if (!user) {
    return (
      <>
        <PageHeader title="Income" subtitle="Earnings report and rank earn cap" />
        <motion.div
          className="panel panel--glow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="eyebrow">Income</div>
          <h2>Connect to view income report</h2>
          <div className="stack">
            <button
              className={`btn btn--primary${connectOpening ? ' is-busy' : ''}`}
              type="button"
              onClick={() => openConnect()}
              disabled={connectOpening}
              aria-busy={connectOpening}
            >
              {connectOpening ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
              {connectOpening ? 'Connecting…' : 'Connect Wallet'}
            </button>
          </div>
        </motion.div>
      </>
    );
  }

  const filtered =
    filter === 'all' ? events : events.filter((e) => e.status === filter);

  const progress =
    cap && cap.max_earnable > 0
      ? Math.min(100, (Number(cap.total_earned) / Number(cap.max_earnable)) * 100)
      : 0;

  return (
    <>
      <PageHeader title="Income" subtitle="Earnings report and rank earn cap" />
      <motion.section
        className="panel panel--glow panel--tint-gold"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="eyebrow">
          <HiOutlineChartBar style={{ marginRight: 4 }} />
          Cap
        </div>
        <h2 style={{ marginBottom: '0.35rem' }}>{cap?.multiplier || 2}x earnings limit</h2>
        <p className="muted" style={{ marginTop: 0, maxWidth: '36rem', lineHeight: 1.55 }}>
          Lifetime income from self daily returns and team activity cannot exceed{' '}
          {cap?.multiplier || 2}× your invested amount
          {cap?.base_multiplier != null && cap?.high_multiplier != null
            ? ` (ranks below ${cap.high_from_rank || 7} use ${cap.base_multiplier}×; rank ${cap.high_from_rank || 7}+ use ${cap.high_multiplier}×)`
            : ''}
          . Further income is skipped when the limit is reached.
        </p>
        <div className="wallet-balances">
          <div className="stat">
            <span>Invested</span>
            <strong>
              {Number(cap?.total_invested || 0).toFixed(2)}
              <em>USDT</em>
            </strong>
          </div>
          <div className="stat">
            <span>Earned</span>
            <strong>{Number(cap?.total_earned || 0).toFixed(4)}</strong>
          </div>
          <div className="stat">
            <span>Max earnable</span>
            <strong>{Number(cap?.max_earnable || 0).toFixed(4)}</strong>
          </div>
          <div className="stat">
            <span>Remaining</span>
            <strong>{Number(cap?.remaining || 0).toFixed(4)}</strong>
          </div>
        </div>
        <div className="cap-bar" aria-hidden>
          <div className="cap-bar__fill" style={{ width: `${progress}%` }} />
        </div>
        {cap?.is_capped ? (
          <p className="ok" style={{ color: 'var(--gold)' }}>
            Cap reached — new income credits are skipped.
          </p>
        ) : null}
      </motion.section>

      <motion.section
        className="panel"
        style={{ marginTop: '1rem' }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '0.75rem',
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: '0.85rem',
          }}
        >
          <h3 style={{ margin: 0 }}>Income report</h3>
          <div className="stack" style={{ marginTop: 0 }}>
            {['all', 'credited', 'partial', 'skipped'].map((f) => (
              <button
                key={f}
                type="button"
                className={`btn ${filter === f ? 'btn--primary' : 'btn--ghost'}`}
                onClick={() => setFilter(f)}
                style={{ padding: '0.45rem 0.75rem', fontSize: '0.78rem' }}
              >
                {f === 'skipped' ? (
                  <>
                    <HiOutlineBan /> Skipped
                  </>
                ) : (
                  f.charAt(0).toUpperCase() + f.slice(1)
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Source</th>
                <th>Requested</th>
                <th>Credited</th>
                <th>Status</th>
                <th>Reason / note</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id}>
                  <td>{new Date(e.created_at).toLocaleString()}</td>
                  <td>
                    {sourceLabel(e.source_type)}
                    {e.source_wallet ? (
                      <div className="muted" style={{ fontSize: '0.75rem' }}>
                        from {shortAddress(e.source_wallet)}
                      </div>
                    ) : null}
                  </td>
                  <td>{Number(e.amount_requested).toFixed(4)}</td>
                  <td>{Number(e.amount_credited).toFixed(4)}</td>
                  <td>
                    <span
                      className={`badge${
                        e.status === 'skipped' || e.status === 'partial' ? ' badge--pending' : ''
                      }`}
                    >
                      {e.status}
                    </span>
                  </td>
                  <td style={{ maxWidth: 280, whiteSpace: 'normal' }}>
                    {e.skip_reason || e.note || '—'}
                  </td>
                </tr>
              ))}
              {!filtered.length ? (
                <tr>
                  <td colSpan={6} className="muted">
                    No income events yet
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </motion.section>
    </>
  );
}
